import { GridColDef } from "@mui/x-data-grid";
import { useLicitacoesDispatch, useLicitacoesState } from "./licitacoes-context";
import { ACTION_TYPES, initialDataRequest } from "./licitacoes-reducer";
import AdminPortalAppService from "../../../../application/platform/admin-portal/admin-portal-app-service";
import { useBaseLayoutController } from "../../../base-layout/context/base-layout-controller";
import { ILicitacaoResponse } from "../../../../model/admin-portal/response/licitacao-response";
import { ITermPaggedRequest } from "../../../../model/admin-portal/request/term-pagged-request";
import { IconButton, Box, Chip, Tooltip } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import { IDocumentoLicitacaoRequest } from "../../../../model/admin-portal/request/documento-licitacao-request";
import { IItemLicitacaoRequest } from "../../../../model/admin-portal/request/item-licitacao-request";
import { ILicitacaoParticipanteRequest } from "../../../../model/admin-portal/request/licitacao-participante-request";

export const useLicitacoesController = () => {
    const state = useLicitacoesState();
    const dispatch = useLicitacoesDispatch();
    const { showMessage, showBackDrop } = useBaseLayoutController();

    const loadData = async (searchRequest: ITermPaggedRequest) => {
        try {
            dispatch({ type: ACTION_TYPES.setIsLoadingData, payload: true });
            const response = await AdminPortalAppService.listLicitacoes(searchRequest);
            dispatch({ type: ACTION_TYPES.setDataPaginatedListResponse, payload: response });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao carregar licitações' });
        } finally {
            dispatch({ type: ACTION_TYPES.setIsLoadingData, payload: false });
        }
    };

    const onChangePagination = async (page: number, pageSize: number) => {
        const newSearch = { ...state.dataSearchRequest, page, pageSize };
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: newSearch });
        await loadData(newSearch);
    };

    const handleOnChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: { ...state.dataSearchRequest, term: e.target.value } });
    };

    const clearFields = async () => {
        const newSearch = { page: 0, pageSize: state.dataSearchRequest.pageSize, term: '' };
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: newSearch });
        await loadData(newSearch);
    };

    const loadLookups = async () => {
        try {
            const [orgaos, modalidades, fases, situacoes, fornecedores] = await Promise.all([
                AdminPortalAppService.listOrgaos(),
                AdminPortalAppService.listModalidadesLicitacao(),
                AdminPortalAppService.listFasesLicitacao(),
                AdminPortalAppService.listSituacoesLicitacao(),
                AdminPortalAppService.listFornecedoresLookup()
            ]);
            dispatch({ type: ACTION_TYPES.setOrgaosList, payload: orgaos });
            dispatch({ type: ACTION_TYPES.setModalidadesList, payload: modalidades });
            dispatch({ type: ACTION_TYPES.setFasesList, payload: fases });
            dispatch({ type: ACTION_TYPES.setSituacoesList, payload: situacoes });
            dispatch({ type: ACTION_TYPES.setFornecedoresList, payload: fornecedores });
        } catch (error: any) {
            showMessage({ type: 'error', message: 'Erro ao carregar listas de apoio (Órgão/Modalidade/Fase/Situação/Fornecedor)' });
        }
    };

    const getData = async (id: number) => {
        try {
            showBackDrop(true);
            const response = await AdminPortalAppService.getLicitacao(id);
            dispatch({ type: ACTION_TYPES.setDataResponse, payload: response });
            dispatch({
                type: ACTION_TYPES.setDataRequest, payload: {
                    id: response.id,
                    idOrgao: response.idOrgao,
                    idModalidadeLicitacao: response.idModalidadeLicitacao,
                    idFaseLicitacao: response.idFaseLicitacao,
                    idSituacaoLicitacao: response.idSituacaoLicitacao,
                    numLicitacao: response.numLicitacao,
                    numProcessoAdministrativo: response.numProcessoAdministrativo || '',
                    numEdital: response.numEdital || '',
                    dtaHoraAbertura: response.dtaHoraAbertura,
                    dtaHomologacao: response.dtaHomologacao || null,
                    localAberturaLicitacao: response.localAberturaLicitacao || '',
                    textoObjetoLicitacao: response.textoObjetoLicitacao,
                    indicativoRegistroPreco: response.indicativoRegistroPreco,
                    valorEstimado: response.valorEstimado ?? undefined,
                    textoJustificativaValorEstimado: response.textoJustificativaValorEstimado || '',
                    valorHomologado: response.valorHomologado || null,
                    pncpUrl: response.pNCPUrl || '',
                    documentos: response.documentos?.map(d => ({ id: d.id, descricao: d.descricao, dtaPublicacao: d.dtaPublicacao, documentoUrl: d.documentoUrl })) || [],
                    itens: response.itens?.map(i => ({ id: i.id, descricao: i.descricao, quantidade: i.quantidade, unidadeMedida: i.unidadeMedida, valorUnitario: i.valorUnitario })) || [],
                    participantes: response.participantes?.map(p => ({ id: p.id, idFornecedor: p.idFornecedor, cnpjcpf: p.cNPJCPF, resultado: p.resultado, valorProposto: p.valorProposto })) || []
                }
            });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao carregar licitação' });
        } finally {
            showBackDrop(false);
        }
    };

    const validFields = (): boolean => {
        const r = state.dataRequest;
        if (!r.idOrgao || !r.idModalidadeLicitacao || !r.idFaseLicitacao || !r.idSituacaoLicitacao) return false;
        if (!r.numLicitacao || r.numLicitacao.trim() === '') return false;
        if (!r.dtaHoraAbertura) return false;
        if (!r.textoObjetoLicitacao || r.textoObjetoLicitacao.trim() === '') return false;
        const hasValorEstimado = r.valorEstimado !== undefined && r.valorEstimado !== null && r.valorEstimado > 0;
        const hasJustificativa = !!r.textoJustificativaValorEstimado && r.textoJustificativaValorEstimado.trim() !== '';
        if (!hasValorEstimado && !hasJustificativa) return false;
        return true;
    };

    const handleOnChangeForm = (field: string, value: any) => {
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, [field]: value } });
    };

    const openForm = (row?: ILicitacaoResponse) => {
        if (row) {
            getData(row.id);
        } else {
            dispatch({ type: ACTION_TYPES.setDataRequest, payload: initialDataRequest });
            dispatch({ type: ACTION_TYPES.setDataResponse, payload: null });
        }
        dispatch({ type: ACTION_TYPES.setShowAddEdit, payload: true });
    };

    const closeForm = () => {
        dispatch({ type: ACTION_TYPES.setShowAddEdit, payload: false });
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: initialDataRequest });
        dispatch({ type: ACTION_TYPES.setDataResponse, payload: null });
    };

    const save = async () => {
        if (!validFields()) {
            showMessage({ type: 'warning', message: 'Preencha todos os campos obrigatórios' });
            return;
        }

        try {
            showBackDrop(true);
            await AdminPortalAppService.addUpdateLicitacao(state.dataRequest);
            showMessage({
                type: 'success',
                message: state.dataRequest.id !== undefined && state.dataRequest.id !== null ? 'Licitação atualizada com sucesso' : 'Licitação criada com sucesso'
            });
            closeForm();
            await loadData(state.dataSearchRequest);
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao salvar licitação' });
        } finally {
            showBackDrop(false);
        }
    };

    //Cada filho é mantido só no estado local até o "Salvar" da Licitação inteira - linhas novas usam id 0
    const saveDocumento = (doc: IDocumentoLicitacaoRequest, index?: number) => {
        const documentos = [...state.dataRequest.documentos];
        if (index !== undefined) documentos[index] = doc; else documentos.push(doc);
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, documentos } });
    };

    const removeDocumento = (index: number) => {
        const documentos = state.dataRequest.documentos.filter((_, i) => i !== index);
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, documentos } });
    };

    const saveItem = (item: IItemLicitacaoRequest, index?: number) => {
        const itens = [...state.dataRequest.itens];
        if (index !== undefined) itens[index] = item; else itens.push(item);
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, itens } });
    };

    const removeItem = (index: number) => {
        const itens = state.dataRequest.itens.filter((_, i) => i !== index);
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, itens } });
    };

    const saveParticipante = (participante: ILicitacaoParticipanteRequest, index?: number) => {
        const participantes = [...state.dataRequest.participantes];
        if (index !== undefined) participantes[index] = participante; else participantes.push(participante);
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, participantes } });
    };

    const removeParticipante = (index: number) => {
        const participantes = state.dataRequest.participantes.filter((_, i) => i !== index);
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, participantes } });
    };

    const getColumnsGrid = (): GridColDef[] => {
        return [
            { field: 'numLicitacao', headerName: 'Nº Licitação', flex: 1, minWidth: 130 },
            { field: 'orgaoName', headerName: 'Órgão', flex: 1, minWidth: 160 },
            { field: 'modalidadeName', headerName: 'Modalidade', flex: 1, minWidth: 150 },
            { field: 'faseName', headerName: 'Fase', width: 120 },
            {
                field: 'situacaoName', headerName: 'Situação', width: 140,
                renderCell: (params) => <Chip label={params.value} size="small" />
            },
            {
                field: 'dtaHoraAbertura', headerName: 'Abertura', width: 120,
                renderCell: (params) => params.value ? new Date(params.value).toLocaleDateString('pt-BR') : ''
            },
            {
                field: 'valorEstimado', headerName: 'Valor Estimado', width: 150, align: 'right', headerAlign: 'right',
                renderCell: (params) => params.value?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
            },
            {
                field: 'actions', headerName: 'Ações', width: 90, align: 'center', headerAlign: 'center', sortable: false,
                renderCell: (params) => (
                    <Box display="flex" gap={1}>
                        <Tooltip title="Editar">
                            <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openForm(params.row); }}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            }
        ];
    };

    return {
        ...state,
        loadData,
        onChangePagination,
        handleOnChangeSearch,
        clearFields,
        loadLookups,
        getData,
        validFields,
        handleOnChangeForm,
        openForm,
        closeForm,
        save,
        getColumnsGrid,
        saveDocumento,
        removeDocumento,
        saveItem,
        removeItem,
        saveParticipante,
        removeParticipante
    };
};
